import { Nullable } from 'types/helpers'
import type { PaginatedResponse, ShallowResponse } from 'types/responses'
import type { VisaStaged } from 'shop/components/visa-block/visa.utils'

import { ApiClient } from 'utils/requests.utils'

export interface TicketAttendee {
  id: number
  fullName: string
  fullAddress: Nullable<string>
  firstName: string
  lastName: string
  email: string
  countryCode: Nullable<string>
  street: Nullable<string>
  streetNumber: number
  zip: Nullable<string>
  city: Nullable<string>
  ticketDateId: number
  phone: Nullable<string>
  salutation: Nullable<string>
  state: Nullable<string>
  personalisationComplete?: boolean
  // Config key for role is `role`; data key is jobTitle.
  company?: Nullable<string>
  jobTitle?: Nullable<string>
  gender?: Nullable<string>
  dateOfBirth?: Nullable<string>
  fields?: Array<{ name: string; required?: boolean; editable?: boolean }>
  // Presence of `visa` gates the block. Staged shape is read-shaped; submit minimises it.
  visa?: VisaStaged
}

export type PersonalisationStatus = 'unassigned' | 'pending' | 'queued' | 'sent' | 'personalised' | 'transferred'

export interface PersonalisationTransferEntry {
  byEmail: string
  at: string
}

export interface EventAttendee {
  id: number
  canEdit: boolean
  // Ticket setting only. Bulk assign hides locked ticket types.
  payerCanEdit?: boolean
  codePrefix: string
  eventName: string
  payerName: string
  shortDescription: string
  state: string
  ticketCode: string
  ticketDate: string
  startDateInZoneFormatted: Nullable<string>
  ticketId: number
  ticketLocationName: string
  ticketName: string
  ticketAttendee: TicketAttendee
  personalisationStatus?: PersonalisationStatus
  personalisationSentAt?: Nullable<string>
  personalisedAt?: Nullable<string>
  // Stays `transferred` once history exists. The badge must not read personalisationStatus (it would vanish when the new holder personalises).
  personalisationDisplayStatus?: PersonalisationStatus
  personalisationTransfers?: PersonalisationTransferEntry[]
}

const BASE_URL = (username) => `/shop/${username}/orders`

export const createEventAttendeesApi = ({ GET, PUT }: ApiClient) => ({
  // Server-side filtering/search contract (ChangeAttendeeModal drives these via SharedStore):
  //   ticket_id=<id>            filter rows to one product ticket (scope)
  //   assignment_status=<value> one of: not_assigned | sent | personalised | transferred
  //                             (not_assigned = no attendee email; the rest map to the derived
  //                             personalisation display status)
  //   query=<text>             search across attendee name / email / ticket code
  // NOTE: the backend `event_attendees` action does not honour these yet — `filter_attributes`
  // there is attribute projection, not row filtering. Until the backend implements them, the
  // filter/search UI will post these params but the list will come back unfiltered.
  fetchList: ({
    username,
    token,
    ...rest
  }: {
    username: string
    token: string
  }): Promise<PaginatedResponse<EventAttendee[]>> => GET(`${BASE_URL(username)}/${token}/event_attendees`, rest),
  saveAttendee: (
    username: string,
    token: string,
    data: { ticketAttendeesAttributes: TicketAttendee[]; recaptcha?: string }
  ): Promise<ShallowResponse<{ success: boolean }>> =>
    PUT(`${BASE_URL(username)}/${token}/update_event_attendees`, data),
})

export type EventAttendeesApi = ReturnType<typeof createEventAttendeesApi>
